<template>
  <view-wrapper name="Users" :loading="loading" :loadingText="loadingText">
    <router-link
      class="mt-3 mb-6 button big primary white"
      to="adduser"
      v-show="disableAddUsers"
      >Add new user</router-link
    >
    <modal
      v-if="showWarning"
      @close="showWarning = false"
      type="warning"
      heading="Warning"
      :description="warningText"
    />

    <wrapper class="mb-5" :check-for-child-focused="true">
      <expandable-table
        class="mt-8"
        :text="'Active users'"
        :columnsToNotSort="columnsToNotSort"
        :headers="headersUsers"
        :data="activeUsers"
        @searchQuery="activeSearchText = $event"
      >
        <template v-slot:column="{ column, child }">
          <div v-if="column === 'actions'" class="text-right">
            <grafokett-button class="small" @click.stop="editUser(child)"
              >Edit <i style="font-size: 2em" class="edit-symbol"
            /></grafokett-button>
          </div>
        </template>
        <template v-slot:child="{ child }">
          <div>
            <p
              v-for="(c, childIndex) in child"
              :key="`${c}Index--${childIndex}`"
            >
              {{ c }}
            </p>
          </div>
        </template>
      </expandable-table>
    </wrapper>

    <wrapper class="mb-5" :check-for-child-focused="true">
      <expandable-table
        :text="'Inactive users'"
        class="pt-10"
        :columnsToNotSort="columnsToNotSort"
        :headers="headersUsers"
        :data="inactiveUsers"
        @searchQuery="inactiveSearchText = $event"
      >
        <template v-slot:column="{ column, child }">
          <div v-if="column === 'actions'" class="text-right">
            <grafokett-button class="small" @click.stop="editUser(child)"
              >Edit <i style="font-size: 2em" class="edit-symbol"
            /></grafokett-button>
          </div>
          <div v-else class="text-primary-light">
            {{ child[column] }}
          </div>
        </template>
        <template v-slot:child="{ child }">
          <div>
            <p
              v-for="(c, childIndex) in child"
              :key="`${c}Index--${childIndex}`"
            >
              {{ c }}
            </p>
          </div>
        </template>
      </expandable-table>
    </wrapper>

    <wrapper class="mb-5" :check-for-child-focused="true">
      <expandable-table
        text="Invited users"
        class="pt-10"
        :data="invitedUsers"
        :columns-to-not-sort="columnsToNotSort"
        :headers="headersInvitations"
        @searchQuery="invitedSearchText = $event"
      >
        <template v-slot:column="{ column, child }">
          <div v-if="column === 'revokeAction'" class="text-right">
            <grafokett-button
              class="small"
              @click.stop="revokeInvitation(child)"
              >Revoke invitation</grafokett-button
            >
          </div>
          <div v-else-if="column === 'resendAction'" class="text-right">
            <grafokett-button
              class="small"
              @click.stop="resendInvitation(child)"
              >Resend invitation</grafokett-button
            >
          </div>
        </template>
      </expandable-table>
    </wrapper>
  </view-wrapper>
</template>

<script>
import GrafokettButton from "../components/forms/Button.vue";
import ExpandableTable from "../components/ExpandabelTable";
import Wrapper from "../components/Wrapper";
import Modal from "../components/Modal";

export default {
  components: {
    GrafokettButton,
    ExpandableTable,
    Wrapper,
    Modal
  },
  data() {
    return {
      headersUsers: {
        name: "Name",
        email: "Email",
        added: "Added",
        actions: ""
      },
      headersInvitations: {
        email: "Email",
        revokeAction: "",
        resendAction: ""
      },
      columnsToNotSort: ["actions", "revokeAction", "resendAction"],
      loading: false,
      loadingText: "List of users",
      userInvitations: [],
      warningText: "",
      showWarning: false,
      disableAddUsers: false,
      activeSearchText: "",
      inactiveSearchText: "",
      invitedSearchText: ""
    };
  },
  async mounted() {
    this.loading = true;
    await this.$store.dispatch("USERS/GET");
    await this.getInvitations();
    await this.getCount();
    this.loading = false;
  },
  methods: {
    async editUser(user) {
      await this.$router.push("/users/" + user.id);
    },
    async getInvitations() {
      const response = await this.$store.$api.get("/api/users/invitations");
      this.userInvitations = response.data.data;
    },
    async resendInvitation(invitation) {
      this.loadingText = "Resending invitation";
      this.loading = true;
      await this.$store.$api.post(
        `/api/users/invitations/resend?invitationId=${invitation.id}`
      );
      this.loading = false;
    },
    async revokeInvitation(invitation) {
      this.loadingText = "Revoking invitation";
      this.loading = true;
      await this.$store.$api.post(
        `/api/users/invitations/revoke?invitationId=${invitation.id}`
      );
      await this.getInvitations();
      await this.getCount();
      this.loading = false;
    },
    async getCount() {
      const responseUsersCount = await this.$store.$api.get("/api/users/count");
      const settingsResponse = await this.$store.$api.get("/api/subscription");
      const maxUsers = settingsResponse.data.data.maxUsers;
      this.disableAddUsers = responseUsersCount.data.data.usersCount < maxUsers;
      this.warningText = `You have reach the maximum amount of users (${maxUsers}) for your organization!`;
      this.showWarning = !this.disableAddUsers;
    },
    searchResult(key, searchText) {
      return key.toLowerCase().indexOf(searchText.toLowerCase()) > -1;
    }
  },
  computed: {
    getUsers() {
      return this.$store.getters["USERS/USERS"].map(u => {
        return {
          name: `${u.firstName} ${u.lastName}`,
          email: u.email,
          added: u.added ? u.added : "",
          roles:
            u.roles.length > 0
              ? u.roles
                  .map(e => e.name)
                  .reduce(
                    (accumulator, currentValue) =>
                      accumulator + ", " + currentValue
                  )
              : "",
          id: u.id,
          disabled: u.disabled,
          expandableData: u.roles.length > 0 ? u.roles.map(e => e.name) : []
        };
      });
    },
    activeUsers() {
      return this.getUsers.filter(
        e =>
          !e.disabled &&
          (this.searchResult(e.name, this.activeSearchText) ||
            this.searchResult(e.email, this.activeSearchText))
      );
    },
    inactiveUsers() {
      return this.getUsers.filter(
        e =>
          e.disabled &&
          (this.searchResult(e.name, this.inactiveSearchText) ||
            this.searchResult(e.email, this.inactiveSearchText))
      );
    },
    invitedUsers() {
      return this.userInvitations.filter(e =>
        this.searchResult(e.email, this.invitedSearchText)
      );
    }
  }
};
</script>
